import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box,
  Paper,
  Button,
  Typography,
  Container,
  Alert,
  CircularProgress,
} from '@mui/material';
import { styled } from '@mui/material/styles';
import { useAuth } from '../contexts/AuthContext';
import loginLogo from '../assets/login_logo.png';

const BackgroundBox = styled(Box)(({ theme }) => ({
  minHeight: '100vh',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  backgroundImage: 'url(/api/placeholder/1920/1080)',
  backgroundSize: 'cover',
  backgroundPosition: 'center',
  backgroundRepeat: 'no-repeat',
  '&::before': {
    content: '""',
    position: 'absolute',
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    backgroundColor: 'rgba(0, 0, 0, 0.5)',
    zIndex: 1,
  },
}));

const LoginContainer = styled(Container)(({ theme }) => ({
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  minHeight: '100vh',
}));

const LogoImage = styled('img')(({ theme }) => ({
  width: 200,
  height: 'auto',
  marginBottom: theme.spacing(3),
}));

const MicrosoftButton = styled(Button)(({ theme }) => ({
  backgroundColor: '#0078d4',
  '&:hover': {
    backgroundColor: '#106ebe',
  },
  '& .MuiButton-startIcon': {
    marginRight: theme.spacing(1),
  },
}));

const EntraIdIcon = () => (
  <svg width="18" height="18" viewBox="0 0 18 18" fill="none" xmlns="http://www.w3.org/2000/svg">
    <path d="M0 9.5L8.5 18V9.5H0Z" fill="#F25022"/>
    <path d="M9.5 18L18 9.5H9.5V18Z" fill="#00A4EF"/>
    <path d="M18 8.5L9.5 0V8.5H18Z" fill="#7FBA00"/>
    <path d="M8.5 0L0 8.5H8.5V0Z" fill="#FFB900"/>
  </svg>
);

const LoginPaper = styled(Paper)(({ theme }) => ({
  padding: theme.spacing(4),
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'center',
  maxWidth: 400,
  width: '100%',
  zIndex: 2,
  position: 'relative',
}));

const LoginPage = () => {
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const { login, isAuthenticated } = useAuth();
  const navigate = useNavigate();

  useEffect(() => {
    if (isAuthenticated) {
      navigate('/', { replace: true });
      return;
    }
    // Auto-login if returning from Microsoft redirect
    const tryAutoLogin = async () => {
      if (window.__msalRedirectResponse) {
        setLoading(true);
        try {
          const result = await login();
          if (result) navigate('/', { replace: true });
        } catch (err) {
          setError(err.message || 'Login failed');
        } finally {
          setLoading(false);
        }
      }
    };
    // Small delay to ensure MSAL has processed the redirect
    const timer = setTimeout(tryAutoLogin, 500);
    return () => clearTimeout(timer);
  }, [isAuthenticated, navigate]);

  const handleLogin = async () => {
    setLoading(true);
    setError('');

    try {
      const result = await login();
      // If null, redirect is happening — don't navigate
      if (result) navigate('/', { replace: true });
    } catch (err) {
      console.error('Login error:', err);
      setError(err.message || 'Login failed. Please try again.');
      setLoading(false);
    }
  };

  return (
    <BackgroundBox>
      <LoginContainer maxWidth="sm">
        <LoginPaper elevation={6}>
          <LogoImage src={loginLogo} alt="eScoreCard Logo" />
          <Typography component="h1" variant="h4" gutterBottom>
            eScoreCard (UAT)
          </Typography>

          <Box sx={{ mt: 3, width: '100%' }}>
            {error && (
              <Alert severity="error" sx={{ mb: 2 }}>
                {error}
              </Alert>
            )}

            <MicrosoftButton
              fullWidth
              variant="contained"
              sx={{ mt: 1, mb: 2 }}
              onClick={handleLogin}
              disabled={loading}
              startIcon={loading ? null : <EntraIdIcon />}
            >
              {loading ? <CircularProgress size={24} /> : 'MS SSO Login'}
            </MicrosoftButton>
          </Box>
        </LoginPaper>
      </LoginContainer>
    </BackgroundBox>
  );
};

export default LoginPage;
